import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faStore,
  faSignal,
  faUserClock,
  faPlugCircleXmark,
  faFileContract,
  faDesktop,
  faCloudArrowUp,
  faRotateRight,
  faCircleCheck,
  faTriangleExclamation,
  faCircleExclamation,
  faCircleInfo,
  faChevronRight,
} from '@fortawesome/free-solid-svg-icons';
import desktopApi, { describeError } from './desktopApi';
import { timeAgo } from './desktopFormat';
import './Desktop.css';

const SEVERITY = {
  critical: { icon: faCircleExclamation, label: 'Urgent' },
  warning: { icon: faTriangleExclamation, label: 'À surveiller' },
  info: { icon: faCircleInfo, label: 'Info' },
};

const REFRESH_MS = 60000;

// The morning check: one screen saying which Lonnii Desktop shops need something.
const DesktopOverview = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      setData(await desktopApi.overview());
      setError(null);
    } catch (e) {
      setError(describeError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(() => load(true), REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  // Each tile opens the shop list already filtered on what it counts.
  const tiles = data ? [
    { label: 'Boutiques', value: data.totalShops, hint: `${data.onlineShops} en ligne · ${data.localShops} locales`, icon: faStore, tone: 'primary', to: '/desktop/shops' },
    { label: "Actives aujourd'hui", value: data.activeToday, hint: 'Connectées ces dernières 24 h', icon: faSignal, tone: 'success', to: '/desktop/shops' },
    { label: 'Inscriptions en attente', value: data.pendingRegistrations, hint: 'À approuver', icon: faUserClock, tone: data.pendingRegistrations ? 'info' : 'neutral', to: '/registrations' },
    { label: 'Hors ligne trop longtemps', value: data.offlineBeyondGrace, hint: 'Application bloquée (mode en ligne)', icon: faPlugCircleXmark, tone: data.offlineBeyondGrace ? 'danger' : 'neutral', to: '/desktop/shops?filter=attention' },
    { label: 'Abonnements à échéance', value: data.subscriptionsEndingSoon, hint: 'Dans les 7 prochains jours', icon: faFileContract, tone: data.subscriptionsEndingSoon ? 'warning' : 'neutral', to: '/desktop/shops?filter=online' },
    { label: 'Limite d\'appareils atteinte', value: data.shopsAtDeviceLimit, hint: `${data.activeDevices} appareils actifs au total`, icon: faDesktop, tone: data.shopsAtDeviceLimit ? 'info' : 'neutral', to: '/desktop/shops' },
    { label: 'Sauvegardes à vérifier', value: data.staleBackups, hint: 'Aucune depuis plus de 3 jours', icon: faCloudArrowUp, tone: data.staleBackups ? 'warning' : 'neutral', to: '/desktop/shops?filter=online' },
  ] : [];

  return (
    <div className="dk-page">
      <div className="dk-header">
        <div>
          <h1>Vue d'ensemble</h1>
          <p>Les boutiques Lonnii Desktop et ce qui demande votre attention.</p>
        </div>
        <div className="dk-header-actions">
          {data && <span className="dk-updated">Mis à jour {timeAgo(data.generatedAt)}</span>}
          <button className="dk-btn" onClick={() => load()} disabled={loading}>
            <FontAwesomeIcon icon={faRotateRight} spin={loading} /> Actualiser
          </button>
        </div>
      </div>

      {error && (
        <div className="dk-error">
          <FontAwesomeIcon icon={faCircleExclamation} />
          <span>{error}</span>
          <button className="dk-btn" onClick={() => load()}>Réessayer</button>
        </div>
      )}

      {loading && !data ? (
        <div className="dk-loading"><div className="spinner"></div><p>Chargement...</p></div>
      ) : data && (
        <>
          <div className="dk-tiles">
            {tiles.map((t) => (
              <Link key={t.label} to={t.to} className={`dk-tile tone-${t.tone}`}>
                <div className="dk-tile-icon"><FontAwesomeIcon icon={t.icon} /></div>
                <div className="dk-tile-body">
                  <span className="dk-tile-value">{t.value}</span>
                  <span className="dk-tile-label">{t.label}</span>
                  <span className="dk-tile-hint">{t.hint}</span>
                </div>
              </Link>
            ))}
          </div>

          <section className="dk-card">
            <div className="dk-card-head">
              <h2>À traiter</h2>
              <span className="dk-count">{data.attention.length}</span>
            </div>

            {data.attention.length === 0 ? (
              <div className="dk-empty">
                <FontAwesomeIcon icon={faCircleCheck} className="dk-empty-ok" />
                <p>Rien à signaler : toutes les boutiques sont en ordre.</p>
              </div>
            ) : (
              <ul className="dk-attention">
                {data.attention.map((item, index) => (
                  <li key={`${item.shopId}-${item.kind}-${index}`}>
                    <Link
                      to={item.kind === 'pending' ? '/registrations' : `/desktop/shops/${item.shopId}`}
                      className={`dk-attention-row sev-${item.severity}`}
                    >
                      <FontAwesomeIcon icon={SEVERITY[item.severity]?.icon || faCircleInfo} className="dk-sev-icon" />
                      <span className="dk-attention-text">
                        <strong>{item.shopName}</strong>
                        <span>{item.message}</span>
                      </span>
                      <span className={`dk-pill sev-${item.severity}`}>{SEVERITY[item.severity]?.label}</span>
                      <FontAwesomeIcon icon={faChevronRight} className="dk-row-chevron" />
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default DesktopOverview;
